import { Stack, Tag } from '@chakra-ui/react';
import Link from 'components/Link';
import { useUserContext } from 'context/UserContextProvider';
import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';

import { IdentificationResultIcon } from '../../components/Icons';
import { StatusTag } from '../../components/StatusTag';
import { DataTable } from '../../components/Table/DataTable';
import { SelectColumnFilter } from '../../components/Table/Filter';
import { MultiSelectDropDown } from '../../components/Table/MultiSelectDropDown';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import { createDropdownPlainOptions, createDropdownTranslatedStaticOptions } from '../../helpers/general';
import useSystemPartnerQuery from '../../hooks/useSystemPartnerQuery';
import useWorkshopsQuery from '../../hooks/useWorkshopsQuery';
import { ContentCard } from '../../layout/ContentCard';

const WorkshopOverviewCard = () => {
  const { t } = useTranslation();
  const { statusCodes, config } = useUserContext();
  const systemPartenerQuery = useSystemPartnerQuery();
  const systemPartnersData = systemPartenerQuery?.data || [];
  const systemPartners = [
    { name: getI18n().t('workshop:system_partner.no_system_partner'), id: 'none' },
    { name: getI18n().t('workshop:system_partner.with_system_partner'), id: 'all' },
    ...systemPartnersData,
  ];

  const workshopCodes = statusCodes?.workshop || {};
  const initialStatus = Object.keys(workshopCodes).filter((status) => status === '300');
  const initialFilters = {
    searchString: '',
    itemsPerPage: 20,
    status: initialStatus || [],
    country: undefined,
    isVerified: undefined,
    systemPartner: undefined,
  };

  const localStorageData = window.localStorage.getItem('workshops_filters');
  // @ts-ignore
  const destringifiedData = JSON.parse(localStorageData) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('workshops_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const [status, setStatus] = useState(destringifiedData?.status || initialFilters?.status);
  const [country, setCountry] = useState(destringifiedData?.country || initialFilters?.country);

  const [systemPartner, setSystemPartner] = useState(destringifiedData?.systemPartner || initialFilters?.systemPartner);
  const [isVerified, setIsVerified] = useState(destringifiedData?.isVerified || initialFilters?.isVerified);
  const [filtersSelected, setFiltersSelected] = useState(false);

  const workshopsQuery = useWorkshopsQuery({
    searchString,
    page,
    itemsPerPage,
    status,
    country,
    isVerified,
    systemPartner,
  });

  // @ts-ignore
  const workshops = workshopsQuery.data?.data || [];

  // @ts-ignore
  const currentPage = workshopsQuery.data?.meta?.current_page - 1;

  const initialLoad =
    searchString === '' && itemsPerPage === 20 && page === 1 && status === undefined && isNil(systemPartner) && isNil(isVerified) && isNil(country);

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage, country, isVerified, systemPartner, status]);
  const allStatusSelected = status.length === Object.keys(workshopCodes).length;

  useEffect(() => {
    setFiltersSelected(!isNil(isVerified) || !isNil(systemPartner) || !isNil(country) || status?.length !== 0);

    window.localStorage.setItem(
      'workshops_filters',
      JSON.stringify({
        searchString: searchString,
        status: status,
        country: country,
        systemPartner: systemPartner,
        isVerified: isVerified,
        itemsPerPage: itemsPerPage,
      }),
    );
  }, [searchString, status, country, itemsPerPage, isVerified, systemPartner]);

  const columns = useMemo(
    () => [
      {
        Header: t('forms:id.label'),
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return <Link to={`/app/op/workshops/${row.original.id}`}>{row.original.user_readable_id}</Link>;
        },
      },
      {
        Header: t('forms:status.label'),
        accessor: 'status',
        shouldFilter: true,
        Filter: () =>
          // @ts-ignore
          //statusFilter(status, setStatus, 'workshop', workshopCodes, allStatusSelected),
          MultiSelectDropDown(status, setStatus, 'workshop', workshopCodes, undefined, t('forms:status.label')),
        // @ts-ignore
        Cell: ({ value }) => <StatusTag resourceKey={'workshop'} currentStatus={value} />,
      },
      {
        Header: t('workshop:email.label'),
        accessor: 'email',
      },
      {
        Header: t('workshop:name.label'),
        accessor: 'name',
        // @ts-ignore
        Cell: ({ value }) => (value ? value : '-'),
      },
      {
        Header: t('workshop:firstname.label'),
        accessor: 'firstname',
      },
      {
        Header: t('workshop:lastname.label'),
        accessor: 'lastname',
      },
      {
        Header: t('workshop:country.label'),
        accessor: 'country',
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: country,
            column,
            dropdownOptions: createDropdownTranslatedStaticOptions({
              records: config?.countries || [],
              translationKey: 'location:countries',
            }),
            onChange: setCountry,
          }),
        // @ts-ignore
        Cell: ({ value }) => (value ? t('location:countries.' + value?.toUpperCase()) : '-'),
      },
      {
        Header: t('workshop:registered_at.label'),
        accessor: (row: { registered_at: string }) => dateTimeTransformer(row.registered_at) ?? '-',
      },

      {
        id: 'button',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Link to={`/app/op/workshops/${row.original.id}`} data-test-id={'edit-' + row.index}>
              {t('common:edit')}
            </Link>
          );
        },
      },
      //Refactor to isVerified
      {
        Header: t('workshop_verification_request:verified'),
        accessor: 'is_verified',
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: isVerified,
            column,
            dropdownOptions: createDropdownPlainOptions({
              records: [
                { label: t('common:yes'), value: true },
                { label: t('common:no'), value: false },
              ],
              valueColumn: 'value',
              labelColumn: 'label',
            }),
            onChange: setIsVerified,
          }),
        Cell: ({ value }: { value: string }) => {
          return t(`common:${value}`);
        },
        // @ts-ignore
      },
      {
        Header: t('workshop:system_partner.label'),
        accessor: 'systempartner_id',
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: systemPartner,
            column,
            dropdownOptions: createDropdownPlainOptions({
              records: systemPartners as any,
              valueColumn: 'id',
              labelColumn: 'name',
            }),
            onChange: setSystemPartner,
          }),
        Cell: (cellProps: { row: any }) => {
          const { row } = cellProps;
          const systemPartnerId = row.original.systempartner_id;
          return (
            <Tag variant={'primary'}>
              {systemPartnerId
                ? systemPartners?.find((partner) => {
                    return partner.id === systemPartnerId;
                  })?.name
                : '-'}
            </Tag>
          );
        },
        // @ts-ignore
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workshopCodes, allStatusSelected, searchString, status, country, isVerified, systemPartner, itemsPerPage, systemPartners],
  );

  return (
    <ContentCard
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('workshop:header')}
      data-test-id="workshops-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              // @ts-ignore
              setStatus(initialFilters?.status);
              setSystemPartner(initialFilters?.systemPartner);
              setIsVerified(initialFilters?.isVerified);
              setCountry(initialFilters?.country);
              setItemsPerPage(initialFilters?.itemsPerPage);
              window.localStorage.setItem('workshops_filters', JSON.stringify(initialFilters));
            }}
            data-test-id="workshops-table"
            data={workshops}
            globalFilter={searchString}
            filtersSelected={filtersSelected}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            autoFocus={true}
            globalFilterPlaceholder={t('workshop:search')}
            //@ts-ignore
            hasFilters={true}
            hasSearchInput={true}
            currentPage={currentPage}
            perPage={itemsPerPage}
            // @ts-ignore
            total={workshopsQuery.data?.meta?.last_page || 1}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={workshopsQuery.isLoading || systemPartenerQuery.isLoading}
            isMoreThenOnePage={
              // @ts-ignore
              !initialLoad || Boolean(workshopsQuery.data?.next_page_url) || currentPage > 0
            }
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default WorkshopOverviewCard;
